import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PageHead } from "@/components/PageHead";
import { getPostsByLang, getFeaturedImage, type WpPost } from "@/lib/wp";
import { readPrerenderJson, PrerenderData } from "@/lib/prerenderData";
import { DEFAULT_LOCALE, type Locale } from "@/hooks/use-locale";
import { usePageSeo } from "@/hooks/use-wp-seo";

function formatDate(d: string) {
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

type BlogIndexProps = {
  lang?: Locale;
};

export default function BlogIndex({ lang = DEFAULT_LOCALE }: BlogIndexProps) {
  const [params] = useSearchParams();
  const pageParam = Number(params.get("page") || 1);
  const basePath = lang === "fr" ? "/fr/blog" : "/blog";
  const dataId = lang === "fr" ? "__PR_BLOG_INDEX_FR__" : "__PR_BLOG_INDEX__";
  const wpLang = lang === DEFAULT_LOCALE ? undefined : lang;
  const { data: pageSeo } = usePageSeo(lang === "fr" ? ["blog-fr", "blog"] : ["blog"], { lang: wpLang });

  // Seed from prerendered JSON for page 1
  const inline = useMemo(() => {
    if (typeof window === "undefined" || pageParam !== 1) return null;
    return readPrerenderJson<{ posts: WpPost[]; totalPages: number }>(dataId);
  }, [pageParam, dataId]);

  const [posts, setPosts] = useState<WpPost[]>(inline?.posts ?? []);
  const [totalPages, setTotalPages] = useState<number>(inline?.totalPages ?? 1);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!inline || pageParam !== 1);

  // Guard StrictMode double fetch in dev
  const didFetchRef = useRef(false);
  useEffect(() => {
    didFetchRef.current = false;
  }, [pageParam]);

  useEffect(() => {
    if (pageParam === 1 && inline?.posts?.length) {
      setLoading(false);
      return;
    }
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    let cancelled = false;
    setLoading(true);
    getPostsByLang(lang === DEFAULT_LOCALE ? undefined : lang, { page: pageParam, perPage: 10 })
      .then(({ posts, totalPages }) => {
        if (cancelled) return;
        setPosts(posts);
        setTotalPages(totalPages || 1);
        setErr(null);
        if (typeof window !== "undefined") (window as any).__PRERENDER_READY__ = true;
      })
      .catch((e) => !cancelled && setErr(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [pageParam, inline]);

  const canonicalPath = pageParam > 1 ? `${basePath}?page=${pageParam}` : basePath;
  const headTitle = pageSeo?.title ?? "Currency orchestration insights and updates | OnPlexus";
  const headDescription = pageSeo?.description ?? undefined;
  const alternates = useMemo(
    () => [
      { hrefLang: "en", href: pageParam > 1 ? `/blog?page=${pageParam}` : "/blog" },
      { hrefLang: "fr", href: pageParam > 1 ? `/fr/blog?page=${pageParam}` : "/fr/blog" },
      { hrefLang: "x-default", href: "/blog" },
    ],
    [pageParam]
  );

  return (
    <div className="min-h-screen bg-onplexus-dark text-white">
      <PageHead
        title={headTitle}
        description={headDescription}
        canonical={canonicalPath}
        alternates={alternates}
        seo={pageSeo}
      />
      <Header />
      <main className="pt-40 pb-24 px-4">
        <div className="max-w-6xl mx-auto space-y-12">
          <section className="rounded-3xl border border-white/10 bg-onplexus-darker/80 px-8 py-12 text-center shadow-[0_40px_120px_-50px_rgba(0,0,0,0.9)] backdrop-blur-sm">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
              Blog
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold text-onplexus-gold">
              Insights &amp; Updates
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base md:text-lg text-white/70">
              Stay current on currency orchestration, payments intelligence, and the technology powering Onplexus.
            </p>
          </section>

          {err && (
            <div className="rounded-3xl border border-red-800/60 bg-red-950/40 px-8 py-6 text-left text-sm text-red-300">
              Error: {err}
            </div>
          )}

          {loading && !err && (
            <div className="rounded-3xl border border-white/10 bg-white/5 px-8 py-10 text-center text-white/70">
              Loading…
            </div>
          )}

          {!loading && !err && (
            <>
              {/* Embed prerender data for hydration skip (page 1 only) */}
              {posts.length > 0 && pageParam === 1 && (
                <PrerenderData id={dataId} value={{ posts, totalPages }} />
              )}

              <ul className="grid gap-8 md:grid-cols-2">
                {posts.map((p) => {
                  const img = getFeaturedImage(p, "medium_large");
                  return (
                    <li
                      key={p.id}
                      className="group overflow-hidden rounded-3xl border border-white/10 bg-onplexus-darker/60 shadow-[0_24px_80px_-40px_rgba(0,0,0,0.8)] transition hover:-translate-y-1 hover:border-onplexus-gold/40"
                    >
                      {img && (
                        <Link to={`${basePath}/${p.slug}`} aria-label={p.title.rendered}>
                          <img
                            src={img}
                            alt=""
                            className="h-52 w-full object-cover transition duration-300 group-hover:opacity-95"
                            loading="lazy"
                          />
                        </Link>
                      )}
                      <div className="flex flex-col gap-4 px-6 py-6">
                        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-white/50">
                          <span>{formatDate(p.date)}</span>
                        </div>
                        <Link to={`${basePath}/${p.slug}`} className="block">
                          <h2
                            className="text-2xl font-semibold leading-snug text-white transition-colors group-hover:text-onplexus-gold"
                            dangerouslySetInnerHTML={{ __html: p.title.rendered }}
                          />
                        </Link>
                        <div
                          className="prose prose-invert text-white/70 line-clamp-4"
                          dangerouslySetInnerHTML={{ __html: p.excerpt?.rendered || "" }}
                        />
                        <div className="pt-2">
                          <Link
                            to={`${basePath}/${p.slug}`}
                            aria-label={`Read article: ${p.title.rendered.replace(/<[^>]+>/g, "").trim() || p.slug}`}
                            className="inline-flex items-center gap-2 rounded-full border border-onplexus-gold/40 px-4 py-2 text-sm font-semibold text-onplexus-gold transition hover:bg-onplexus-gold hover:text-onplexus-dark"
                          >
                            Read article
                            <span aria-hidden>→</span>
                          </Link>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {totalPages > 1 && (
                <nav className="flex items-center justify-between rounded-3xl border border-white/10 bg-onplexus-darker/60 px-6 py-4 text-sm text-white/70">
                  <PageLink disabled={pageParam <= 1} to={`${basePath}?page=${pageParam - 1}`}>
                    ← Newer posts
                  </PageLink>
                  <span className="text-white/60">Page {pageParam} / {totalPages}</span>
                  <PageLink disabled={pageParam >= totalPages} to={`${basePath}?page=${pageParam + 1}`}>
                    Older posts →
                  </PageLink>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PageLink({ to, children, disabled }: { to: string; children: React.ReactNode; disabled?: boolean }) {
  if (disabled) {
    return <span className="inline-flex items-center gap-2 opacity-40">{children}</span>;
  }
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-semibold text-white transition hover:border-onplexus-gold/50 hover:text-onplexus-gold"
    >
      {children}
    </Link>
  );
}
